/**
 * @file CashCheck.js
 * @description Page component for processing check cashing transactions, capturing check photos, performing OCR scan, and managing payee/bank info.
 */

import "../css/pages/CashCheck.css";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import MasterDropdown from "../components/MasterDropdown";
import DocumentCameraModal from "../components/DocumentCameraModal";
import DateInput from "../components/DateInput";
import {
  cashCheque,
  getCustomers,
  getCustomer,
  getCustomerCheques,
  uploadChequeFront,
  uploadChequeBack,
  scanChequeImage,
  cropCheckImage,
  getBanks,
  getCompanies,
  getCompanyByName,
  setCompanyReferenceImage,
  createCompany,
  createBank,
} from "../api/services";
import {
  DocumentImageError,
  fileToBase64,
  hasReadableScanData,
  isLowConfidence,
} from "../utils/documentImage";
import toast from "react-hot-toast";
import { getTodayChicago } from "../utils/formatDate";

/**
 * Formats a numeric value as a US currency string.
 * @param {number|string} n - Number or string amount.
 * @returns {string} Formatted currency string or fallback dash.
 */
const fmt = (n) =>
  n !== "" && n !== null && n !== undefined
    ? "$" +
      Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

const defaultForm = {
  customer: "",
  checkNumber: "",
  checkType: "Personal",
  amount: "",
  fee: "",
  checkDate: getTodayChicago(),
  cashedOn: getTodayChicago(), // Transaction date (when actually cashed)
  maker: "", // payer / issuer name
  makerAddress: "", // issuer address
  licenseNumber: "", // customer's ID/license number written on check
  status: "Cashed",
  isLoan: false,
  notes: "",
  bank: { name: "", routingNumber: "", accountNumber: "" },
  verifyCode: { grade: "", code: "" },
};

/**
 * SectionHeader component for rendering styled group headers with icons.
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Header icon element.
 * @param {string} props.title - Title text.
 * @param {string} [props.sub] - Optional subtitle text.
 */
const SectionHeader = ({ icon, title, sub }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 16,
      paddingBottom: 10,
      borderBottom: "1.5px solid var(--gray-3)",
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        background: "var(--primary-light)",
        borderRadius: 9,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 17,
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        {title}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--gray-1)" }}>{sub}</div>}
    </div>
  </div>
);
void SectionHeader;

/**
 * Modal dialog component for quickly adding a new bank to the master bank list.
 * @component
 * @param {Object} props
 * @param {Function} props.onClose - Callback to close modal.
 * @param {Function} props.onSaved - Callback invoked with created bank object.
 */
function AddBankModal({ onClose, onSaved }) {
  const [name, setName] = React.useState("");
  const [routingNumber, setRouting] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Bank name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await createBank({ name: name.trim(), routingNumber });
      toast.success(`✅ Bank "${name}" added!`);
      onSaved(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add bank");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 14,
          padding: 28,
          width: 420,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 17,
            marginBottom: 18,
          }}
        >
          🏦 Add New Bank
        </div>
        <div className="form-group">
          <label className="form-label">
            Bank Name <span style={{ color: "var(--danger)" }}>*</span>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hancock Bank"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label className="form-label">Routing Number</label>
          <input
            className="form-control"
            value={routingNumber}
            onChange={(e) => setRouting(e.target.value)}
            placeholder="062000019"
            style={{ fontFamily: "monospace" }}
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "✅ Save Bank"}
          </button>
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Main Check Cashing page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function CashCheck() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillId = searchParams.get("customerId");

  const [form, setForm] = useState({
    ...defaultForm,
    customer: prefillId || "",
  });
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchCust, setSearchCust] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDrop, setShowDrop] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(!!prefillId); // true while fetching pre-selected customer
  const [bankList, setBankList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [checkFrontFile, setCheckFrontFile] = useState(null);
  const [checkBackFile, setCheckBackFile] = useState(null);
  const [checkFrontPreview, setCheckFrontPreview] = useState(null);
  const [checkBackPreview, setCheckBackPreview] = useState(null);
  const [scanningCheque, setScanningCheque] = useState(false);
  const [chequeScanned, setChequeScanned] = useState(false);
  const [showFrontCamera, setShowFrontCamera] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  const [referenceCompany, setReferenceCompany] = useState(null); // {_id, name, referenceCheckImage}
  const [blockedMaker, setBlockedMaker] = useState(null); // blocked company object if maker is blocked
  const frontRef = useRef();
  const backRef = useRef();

  // Load master data — guard against React StrictMode double-mount
  const masterDataLoadedRef = useRef(false);
  const loadBanks = () =>
    getBanks({ limit: 9999 })
      .then((r) => setBankList(r.data.data || []))
      .catch(() => {});
  useEffect(() => {
    if (masterDataLoadedRef.current) return;
    masterDataLoadedRef.current = true;
    loadBanks();
    getCompanies({ limit: 9999 })
      .then((r) => setCompanyList(r.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (prefillId)
      getCustomer(prefillId)
        .then((r) => {
          const c = r.data.data;
          if (!c) {
            setPrefillLoading(false);
            return;
          }
          setSelectedCustomer(c);
          set("customer", c._id);
          // Auto-fill side-effects (same as selectCustomer)
          if (c.employer?.name) set("maker", c.employer.name);
          if (c.identifications?.[0]?.idNumber)
            set("licenseNumber", c.identifications[0].idNumber);
          setPrefillLoading(false);
        })
        .catch(() => setPrefillLoading(false));
  }, [prefillId]);

  useEffect(() => {
    if (searchCust.length >= 2) {
      getCustomers({ search: searchCust, limit: 10 }).then((r) =>
        setCustomers(r.data.data),
      );
    } else {
      setCustomers([]);
    }
  }, [searchCust]);

  // Look up reference check image for the maker/company when it changes
  // Also check if the selected maker is a blocked company
  useEffect(() => {
    const name = form.maker?.trim();
    if (!name || name.length < 2) {
      setReferenceCompany(null);
      setBlockedMaker(null);
      return;
    }

    // Instant blocked check from already-loaded companyList
    const matched = companyList.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );
    if (matched?.isBlocked) {
      setBlockedMaker(matched);
    } else {
      setBlockedMaker(null);
    }

    const timer = setTimeout(() => {
      getCompanyByName(name)
        .then((r) => {
          const company = r.data.data || null;
          setReferenceCompany(company);
          if (company?.isBlocked) setBlockedMaker(company);
        })
        .catch(() => setReferenceCompany(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [form.maker, companyList]);

  // ── Add/sync a company to Company Master from the maker dropdown ──────────
  const refreshCompanyList = () =>
    getCompanies({ limit: 9999 })
      .then((r) => setCompanyList(r.data.data || []))
      .catch(() => {});

  // Returns the company object (existing or newly created).
  // Toasts only when a NEW company is created — silent when already exists.
  const syncMakerToCompanyMaster = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const r = await getCompanyByName(trimmed);
      if (r.data.data) {
        // Already exists — refresh list so dropdown picks it up, no toast
        await refreshCompanyList();
        return r.data.data;
      }
      // Not found — create it
      const created = await createCompany({
        name: trimmed,
        notes: "Added from Cash a Check",
      });
      toast.success(`🏢 "${trimmed}" added to Company Master`, {
        duration: 3000,
      });
      await refreshCompanyList();
      return created.data.data;
    } catch (err) {
      const msg = err?.response?.data?.message || "";
      // If duplicate error, it was just created by another path — fetch and return it
      if (msg.includes("already exists") || err?.response?.status === 400) {
        await refreshCompanyList();
        return null;
      }
      console.error("syncMakerToCompanyMaster error:", err);
      return null;
    }
  };

  // Legacy wrapper used by MasterDropdown onAddNew (manual "Add" click — always toast)
  const handleAddToCompanyMaster = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const r = await getCompanyByName(trimmed);
      if (r.data.data) {
        toast.success(`🏢 "${trimmed}" already in Company Master`);
        await refreshCompanyList();
        return;
      }
      await createCompany({ name: trimmed, notes: "Added from Cash a Check" });
      toast.success(`🏢 "${trimmed}" added to Company Master`);
      await refreshCompanyList();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add company");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBank = (k, v) =>
    setForm((f) => ({ ...f, bank: { ...f.bank, [k]: v } }));
  const setVC = (k, v) =>
    setForm((f) => ({ ...f, verifyCode: { ...f.verifyCode, [k]: v } }));
  void setVC;

  const selectCustomer = async (c) => {
    setSelectedCustomer(c);
    set("customer", c._id);
    setShowDrop(false);
    setSearchCust("");
    // Auto-fill employer as maker if known
    if (c.employer?.name && !form.maker) set("maker", c.employer.name);
    // Auto-fill license number from customer's primary ID
    if (c.identifications?.[0]?.idNumber) {
      set("licenseNumber", c.identifications[0].idNumber);
    }
    // Warn if blocked
    if (c.isBlocked) {
      toast.error(
        `⛔ ${c.firstName} ${c.lastName} is a BLOCKED customer. Transactions are not allowed.`,
      );
    }
    // Auto-fill bank from customer's most recent transaction
    try {
      const res = await getCustomerCheques(c._id, {
        limit: 10,
        sort: "-createdAt",
      });
      const cheques = res.data.data || res.data || [];
      const lastWithBank = cheques.find((ch) => ch.bank?.name);
      if (lastWithBank?.bank) {
        const b = lastWithBank.bank;
        setForm((f) => ({
          ...f,
          bank: {
            name: b.name || f.bank.name,
            routingNumber: b.routingNumber || f.bank.routingNumber,
            accountNumber: b.accountNumber || f.bank.accountNumber,
          },
        }));
      }
    } catch (_) {
      // Silently ignore — bank stays empty for manual entry
    }
  };

  // ── Auto-crop a check photo via the Python crop API (server-side —
  // works the same on mobile and desktop, unlike the old canvas-only
  // approach which had to be skipped on mobile). Fails open: if the crop
  // service errors or times out, the original file is returned untouched
  // so the user is never blocked.
  const cropWithApi = async (file, side = "front") => {
    try {
      const base64 = await fileToBase64(file);
      const res = await cropCheckImage(base64, file.type || "image/jpeg");
      const d = res.data;
      if (!d?.success || !d?.image) return file;

      const mimeType = d.mimeType || "image/jpeg";
      const blob = await fetch(`data:${mimeType};base64,${d.image}`).then((r) =>
        r.blob(),
      );
      const ext = mimeType.includes("png") ? "png" : "jpg";
      return new File(
        [blob],
        `cheque-${side}-${Date.now()}-cropped.${ext}`,
        { type: mimeType, lastModified: Date.now() },
      );
    } catch (err) {
      console.warn("Check crop failed, using original image:", err.message);
      return file;
    }
  };

  const handlePhoto = (e, side) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    const url = URL.createObjectURL(file);
    if (side === "front") {
      setCheckFrontFile(file);
      setCheckFrontPreview(url);
    } else {
      setCheckBackFile(file);
      setCheckBackPreview(url);
    }

    // Crop in the background, then swap the preview/file for the cropped
    // version once it's ready.
    cropWithApi(file, side).then((croppedFile) => {
      if (croppedFile === file) return; // crop skipped/failed — original already showing
      const croppedUrl = URL.createObjectURL(croppedFile);
      if (side === "front") {
        setCheckFrontFile(croppedFile);
        setCheckFrontPreview(croppedUrl);
      } else {
        setCheckBackFile(croppedFile);
        setCheckBackPreview(croppedUrl);
      }
      URL.revokeObjectURL(url);
    });
  };

  // ── Scan cheque image and auto-fill fields ─────────────────────────────────
  const legacyHandleChequeScan = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (side === "front") {
      setCheckFrontFile(file);
      setCheckFrontPreview(url);
    } else {
      setCheckBackFile(file);
      setCheckBackPreview(url);
    }

    setScanningCheque(true);
    setChequeScanned(false);
    const toastId = toast.loading(
      "🔍 Scanning cheque — auto-filling fields...",
    );
    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await scanChequeImage(base64, file.type || "image/jpeg");
      const d = res.data?.data;

      if (d && Object.keys(d).some((k) => d[k])) {
        const filled = await applyChequeScanData(d);
        setChequeScanned(true);
        toast.success(`✅ Cheque scanned! ${filled.length} fields filled.`, {
          id: toastId,
          duration: 4000,
        });
      } else {
        toast.error("Could not read cheque fields. Please fill manually.", {
          id: toastId,
        });
      }
    } catch (err) {
      toast.error("Cheque scan failed. Please fill manually.", { id: toastId });
      console.error("Cheque scan error:", err);
    } finally {
      setScanningCheque(false);
    }
  };
  void legacyHandleChequeScan;

  const applyChequeScanData = async (d) => {
    const filled = [];

    if (d.maker) {
      const makerName = d.maker.trim();
      filled.push(`Maker: ${makerName}`);

      // 1. Set the maker string immediately so the field shows
      set("maker", makerName);

      // 2. Silently sync to Company Master (find or create), then refresh dropdown
      //    The MasterDropdown useEffect will auto-select once companyList updates
      syncMakerToCompanyMaster(makerName);
    }

    if (d.makerAddress) set("makerAddress", d.makerAddress);

    if (d.bankName) {
      setBank("name", d.bankName);
      filled.push(`Bank: ${d.bankName}`);
    }
    if (d.routingNumber) setBank("routingNumber", d.routingNumber);
    if (d.accountNumber) setBank("accountNumber", d.accountNumber);
    if (d.checkNumber) {
      set("checkNumber", d.checkNumber);
      filled.push(`Check #: ${d.checkNumber}`);
    }
    if (d.amount) {
      set("amount", d.amount);
      filled.push(`Amount: $${d.amount}`);
    }
    if (d.date) {
      set("checkDate", d.date);
      filled.push(`Date: ${d.date}`);
    }
    if (d.licenseNumber) {
      set("licenseNumber", d.licenseNumber);
      filled.push(`License #: ${d.licenseNumber}`);
    }
    return filled;
  };

  const scanPreparedCheque = async (prepared) => {
    const first = await scanChequeImage(prepared.base64, prepared.mimeType);
    const firstData = first.data?.data;
    if (hasReadableScanData(firstData) && !isLowConfidence(firstData))
      return firstData;

    console.warn("Cheque OCR low confidence; retrying normalized image", {
      firstData,
      diagnostics: prepared.diagnostics,
    });
    const retry = await scanChequeImage(
      prepared.fallbackBase64,
      prepared.mimeType,
    );
    return retry.data?.data || firstData;
  };
  void scanPreparedCheque;

  // Normalize camera/gallery images before OCR so both paths send the same JPEG.
  const scanChequeFile = async (file, side = "front", source = "gallery") => {
    if (!file) return;
    setScanningCheque(false);
    setChequeScanned(false);
    setScanningCheque(true);
    const toastId = toast.loading("Scanning cheque - auto-filling fields...");

    // Set preview immediately so user sees their photo right away
    const previewUrl = URL.createObjectURL(file);
    if (side === "front") {
      if (checkFrontPreview) URL.revokeObjectURL(checkFrontPreview);
      setCheckFrontFile(file);
      setCheckFrontPreview(previewUrl);
    } else {
      if (checkBackPreview) URL.revokeObjectURL(checkBackPreview);
      setCheckBackFile(file);
      setCheckBackPreview(previewUrl);
    }

    try {
      // Step 1 — CROP: send the raw photo to the Python crop API (via the
      // backend proxy). Works identically on mobile and desktop, so we no
      // longer need to skip processing on mobile or rely on the fragile
      // local canvas heuristic.
      const croppedFile = await cropWithApi(file, side);

      const croppedPreviewUrl = URL.createObjectURL(croppedFile);
      if (side === "front") {
        if (checkFrontPreview) URL.revokeObjectURL(checkFrontPreview);
        setCheckFrontFile(croppedFile);
        setCheckFrontPreview(croppedPreviewUrl);
      } else {
        if (checkBackPreview) URL.revokeObjectURL(checkBackPreview);
        setCheckBackFile(croppedFile);
        setCheckBackPreview(croppedPreviewUrl);
      }
      URL.revokeObjectURL(previewUrl);

      const base64 = await fileToBase64(croppedFile);
      const mimeType = croppedFile.type || "image/jpeg";

      // Step 2 — CHECK: run OCR / field extraction on the cropped image.
      const res = await scanChequeImage(base64, mimeType);
      const d = res.data?.data;

      if (hasReadableScanData(d)) {
        const filled = await applyChequeScanData(d);
        setChequeScanned(true);
        toast.success(`Cheque scanned! ${filled.length} fields filled.`, {
          id: toastId,
          duration: 4000,
        });
      } else {
        toast.error("Could not read cheque fields. Please fill manually.", {
          id: toastId,
        });
      }
    } catch (err) {
      console.error("Cheque scan error:", {
        source,
        side,
        error: err,
        response: err.response?.data,
      });
      let message = "Cheque scan failed. Please fill manually.";
      if (err instanceof DocumentImageError && err.code === "BLURRY_IMAGE") {
        message = "Image is too blurry. Hold steady and retake the photo.";
      } else if (!navigator.onLine) {
        message = "No internet connection. Please check your network.";
      } else if (err?.response?.status >= 500) {
        message = "Server error. Please try again in a moment.";
      }
      toast.error(message, { id: toastId });
    } finally {
      setScanningCheque(false);
    }
  };

  const handleChequeScan = async (e, side) => {
    const file = e.target.files[0];
    e.target.value = "";
    await scanChequeFile(file, side, "gallery");
  };

  const amount = parseFloat(form.amount) || 0;
  const fee = parseFloat(form.fee) || 0;
  const payOut =
    amount > 0 && form.fee !== "" ? (amount - fee).toFixed(2) : null;

  // Auto-calculate fee (example: 2% of amount, min $5)
  const autoCalcFee = () => {
    if (!amount) return;
    const calculated = Math.max(5, amount * 0.02).toFixed(2);
    set("fee", calculated);
    toast.success(`Fee auto-set to ${fmt(calculated)} (2% of amount)`);
  };

  const handleSubmit = async () => {
    if (!form.customer) {
      toast.error("Please select a customer");
      return;
    }
    if (selectedCustomer?.isBlocked) {
      toast.error(
        `⛔ Transaction blocked. ${selectedCustomer.firstName} ${selectedCustomer.lastName} is a blocked customer.`,
      );
      return;
    }
    if (blockedMaker) {
      toast.error(
        `⛔ Transaction blocked. "${blockedMaker.name}" is on the blocked companies list.${blockedMaker.blockedReason ? ` Reason: ${blockedMaker.blockedReason}` : ""}`,
        { duration: 6000 },
      );
      return;
    }
    if (!form.checkNumber) {
      toast.error("Check number is required");
      return;
    }
    if (!form.maker || !form.maker.trim()) {
      toast.error("Company Name (Maker) is required");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Enter a valid check amount");
      return;
    }
    if (form.fee === "" || fee < 0) {
      toast.error("Enter the fee amount");
      return;
    }

    setLoading(true);
    try {
      const res = await cashCheque({ ...form, amount, fee });
      const chequeId = res.data.data._id;
      const uploads = [];
      if (checkFrontFile)
        uploads.push(uploadChequeFront(chequeId, checkFrontFile));
      if (checkBackFile)
        uploads.push(uploadChequeBack(chequeId, checkBackFile));
      if (uploads.length > 0) {
        const uploadToast = toast.loading("Saving check images...");
        try {
          const uploadResults = await Promise.all(uploads);
          toast.success("✅ Check cashed and images saved!", {
            id: uploadToast,
          });
          // Save reference image for company signature comparison (first image only)
          if (
            checkFrontFile &&
            referenceCompany?._id &&
            !referenceCompany.referenceCheckImage
          ) {
            const frontUrl = uploadResults[0]?.data?.url;
            if (frontUrl) {
              setCompanyReferenceImage(
                referenceCompany._id,
                frontUrl,
                chequeId,
              ).catch(() => {});
            }
          }
        } catch (uploadErr) {
          toast.error(
            "Check cashed but image upload failed. Please re-upload from the transaction detail page.",
            { id: uploadToast, duration: 6000 },
          );
          console.error(
            "Image upload error:",
            uploadErr?.response?.data || uploadErr,
          );
        }
      } else {
        toast.success("✅ Check cashed successfully!");
      }
      navigate(`/cheques/${chequeId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Transaction failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Cash a Check">
      <DocumentCameraModal
        open={showFrontCamera}
        title="Scan Check Front"
        label="check front"
        aspectRatio={2.15}
        onClose={() => setShowFrontCamera(false)}
        onCapture={(file) => scanChequeFile(file, "front", "camera")}
      />
      {showAddBank && (
        <AddBankModal
          onClose={() => setShowAddBank(false)}
          onSaved={(bank) => {
            setShowAddBank(false);
            loadBanks();
            setBank("name", bank.name);
            if (bank.routingNumber)
              setBank("routingNumber", bank.routingNumber);
          }}
        />
      )}
      <div className="flex items-center gap-3 mb-4">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate("/cheques")}
        >
          ← Back
        </button>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 20,
            fontWeight: 800,
          }}
        >
          💵 Cash a Check
        </h2>
        <span
          style={{
            fontSize: 12,
            color: "var(--gray-1)",
            background: "var(--gray-4)",
            padding: "3px 10px",
            borderRadius: 20,
          }}
        >
          Copy info from physical check
        </span>
      </div>

      <div className="cashcheck-layout" style={{ display: "grid", gap: 12 }}>
        {/* ── LEFT COLUMN ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* STEP 1 — CUSTOMER */}
          <div className="card">
            <div
              className="card-header"
              style={{
                background: "linear-gradient(135deg,#0f3d2b,#1a7a4a)",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    color: "white",
                  }}
                >
                  1
                </div>
                <h3 style={{ color: "white" }}>
                  Customer — Pay To The Order Of
                </h3>
              </div>
            </div>
            <div className="card-body">
              {prefillLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "16px",
                    background: "var(--primary-light)",
                    borderRadius: 10,
                    border: "1.5px solid rgba(26,122,74,0.3)",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "rgba(26,122,74,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                    }}
                  >
                    ⏳
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--gray-1)",
                      fontWeight: 500,
                    }}
                  >
                    Loading customer…
                  </div>
                </div>
              ) : selectedCustomer ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    background: selectedCustomer.isBlocked
                      ? "#fef2f2"
                      : "var(--primary-light)",
                    borderRadius: 10,
                    border: selectedCustomer.isBlocked
                      ? "2px solid #dc2626"
                      : "1.5px solid rgba(26,122,74,0.3)",
                  }}
                >
                  <div>
                    {selectedCustomer.isBlocked && (
                      <div
                        style={{
                          background: "#dc2626",
                          color: "white",
                          borderRadius: 6,
                          padding: "6px 12px",
                          marginBottom: 10,
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        🚫 BLOCKED CUSTOMER — Transactions are NOT allowed
                      </div>
                    )}
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 17,
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      {selectedCustomer.firstName} {selectedCustomer.middleName}{" "}
                      {selectedCustomer.lastName}
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--gray-1)",
                        marginTop: 3,
                      }}
                    >
                      {selectedCustomer.identifications?.[0]?.type}:{" "}
                      <strong style={{ fontFamily: "monospace" }}>
                        {selectedCustomer.identifications?.[0]?.idNumber}
                      </strong>
                      {" · "}DOB:{" "}
                      {selectedCustomer.dateOfBirth
                        ? new Date(
                            selectedCustomer.dateOfBirth + "T12:00:00",
                          ).toLocaleDateString("en-US", {
                            timeZone: "America/Chicago",
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          })
                        : "—"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--gray-1)",
                        marginTop: 2,
                      }}
                    >
                      📞 {selectedCustomer.contacts?.[0]?.value || "No phone"}
                      {" · "}📍 {selectedCustomer.address?.city},{" "}
                      {selectedCustomer.address?.state}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--primary)",
                        marginTop: 3,
                        fontWeight: 600,
                      }}
                    >
                      Yearly Activity:{" "}
                      {fmt(selectedCustomer.yearlyActivityTotal)}
                    </div>
                    {/* Check if ID expired */}
                    {selectedCustomer.identifications?.[0]?.expiryDate &&
                      new Date(selectedCustomer.identifications[0].expiryDate) <
                        new Date() && (
                        <div
                          style={{
                            marginTop: 6,
                            padding: "5px 10px",
                            background: "var(--danger-light)",
                            color: "var(--danger)",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          ⚠️ ID EXPIRED — Verify alternate identification before
                          cashing
                        </div>
                      )}
                  </div>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      setSelectedCustomer(null);
                      set("customer", "");
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    className="form-control"
                    placeholder="🔍 Search by name, ID number, or phone..."
                    value={searchCust}
                    onChange={(e) => {
                      setSearchCust(e.target.value);
                      setShowDrop(true);
                    }}
                    onFocus={() => setShowDrop(true)}
                    style={{ fontSize: 14 }}
                  />
                  {showDrop && customers.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "white",
                        border: "1.5px solid var(--gray-3)",
                        borderRadius: 10,
                        boxShadow: "var(--shadow-lg)",
                        zIndex: 50,
                        maxHeight: 300,
                        overflowY: "auto",
                        marginTop: 4,
                      }}
                    >
                      {customers.map((c) => (
                        <div
                          key={c._id}
                          onClick={() => selectCustomer(c)}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--gray-4)",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "var(--primary-light)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "white")
                          }
                        >
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            {c.firstName} {c.middleName} {c.lastName}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--gray-1)",
                              display: "flex",
                              gap: 12,
                              marginTop: 2,
                            }}
                          >
                            <span>
                              {c.identifications?.[0]?.type}:{" "}
                              <strong style={{ fontFamily: "monospace" }}>
                                {c.identifications?.[0]?.idNumber}
                              </strong>
                            </span>
                            <span>📞 {c.contacts?.[0]?.value}</span>
                            <span>
                              📍 {c.address?.city}, {c.address?.state}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12.5,
                      color: "var(--gray-1)",
                    }}
                  >
                    Customer not in system?{" "}
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: "2px 8px", fontSize: 12.5 }}
                      onClick={() => navigate("/customers/new")}
                    >
                      ➕ Add New Customer First
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 — CHECK DETAILS */}
          <div className="card">
            <div
              className="card-header"
              style={{
                background: "linear-gradient(135deg,#1a3a5c,#1a5c8a)",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    color: "white",
                  }}
                >
                  2
                </div>
                <h3 style={{ color: "white" }}>Check Information</h3>
                <span
                  style={{
                    fontSize: 11.5,
                    color: "rgba(255,255,255,0.6)",
                    marginLeft: 4,
                  }}
                >
                  From the physical check
                </span>
              </div>
            </div>
            <div className="card-body">
              {/* Check photo upload + scan */}
              {/* Scan status banner */}
              {(scanningCheque || chequeScanned) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 10,
                    marginBottom: 14,
                    background: scanningCheque ? "#f0fdf4" : "#ecfdf5",
                    border: `1.5px dashed ${scanningCheque ? "#16a34a" : "#10b981"}`,
                  }}
                >
                  {scanningCheque ? (
                    <>
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          border: "2.5px solid #16a34a",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#15803d",
                        }}
                      >
                        🤖 AI Reading Cheque — auto-filling fields...
                      </span>
                    </>
                  ) : (
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#059669",
                      }}
                    >
                      ✅ Cheque scanned! Review the auto-filled fields below.
                    </span>
                  )}
                </div>
              )}

              <div className="scan-shell">
                {/* Front — click to scan+upload */}
                <div className="scan-tile">
                  <div
                    className={`scan-preview ${scanningCheque ? "is-active" : ""}`}
                    style={{
                      border: scanningCheque
                        ? "1.5px solid #16a34a"
                        : "1.5px dashed var(--gray-3)",
                      background: scanningCheque ? "#f0fdf4" : "var(--gray-4)",
                    }}
                    onClick={() => !scanningCheque && setShowFrontCamera(true)}
                  >
                    {checkFrontPreview ? (
                      <img
                        src={checkFrontPreview}
                        alt="Front"
                        style={{
                          width: "100%",
                        }}
                      />
                    ) : (
                      <div style={{ padding: "12px 0" }}>
                        <div style={{ fontSize: 28 }}>📄</div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--gray-1)",
                            marginTop: 4,
                            fontWeight: 600,
                          }}
                        >
                          Check Front
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: "var(--primary)",
                            marginTop: 2,
                          }}
                        >
                          📸 Tap to scan & auto-fill
                        </div>
                      </div>
                    )}
                  </div>
                  {!scanningCheque && (
                    <div className="scan-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: 11.5, fontWeight: 700 }}
                        onClick={() => setShowFrontCamera(true)}
                      >
                        Scan Check
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        style={{
                          fontSize: 11.5,
                          color: "var(--primary)",
                          fontWeight: 700,
                        }}
                        onClick={() => frontRef.current.click()}
                      >
                        {checkFrontPreview ? "Choose Again" : "Gallery"}
                      </button>
                    </div>
                  )}
                  {false && checkFrontPreview && !scanningCheque && (
                    <button
                      className="btn btn-sm btn-outline w-full"
                      style={{
                        marginTop: 6,
                        fontSize: 11.5,
                        color: "var(--primary)",
                        fontWeight: 700,
                      }}
                      onClick={() => frontRef.current.click()}
                    >
                      🔄 Re-scan Front
                    </button>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={frontRef}
                    onChange={(e) => handleChequeScan(e, "front")}
                    style={{ display: "none" }}
                  />
                </div>

                {/* Back */}
                <div className="scan-tile">
                  <div
                    className="scan-preview"
                    style={{
                      border: "1.5px dashed var(--gray-3)",
                      background: "var(--gray-4)",
                    }}
                    onClick={() => backRef.current.click()}
                  >
                    {checkBackPreview ? (
                      <img
                        src={checkBackPreview}
                        alt="Back"
                        style={{
                          width: "100%",
                        }}
                      />
                    ) : (
                      <div style={{ padding: "12px 0" }}>
                        <div style={{ fontSize: 28 }}>📃</div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--gray-1)",
                            marginTop: 4,
                            fontWeight: 600,
                          }}
                        >
                          Check Back
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: "var(--gray-2)",
                            marginTop: 2,
                          }}
                        >
                          Endorsement side
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={backRef}
                    onChange={(e) => handlePhoto(e, "back")}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              {/* Check # + Date on Check + Cashed On + Maker */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 2fr",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">
                    Check Number{" "}
                    <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.checkNumber}
                    onChange={(e) => set("checkNumber", e.target.value)}
                    placeholder="3061 / 319"
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      fontSize: 15,
                      letterSpacing: 1,
                    }}
                  />
                </div>
                {/* Check Type — hidden */}
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">
                    Date on Check{" "}
                    <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <DateInput
                    value={form.checkDate}
                    onChange={(e) => set("checkDate", e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">
                    Cashed On <span style={{ color: "var(--danger)" }}>*</span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--gray-1)",
                        fontWeight: 400,
                        marginLeft: 4,
                      }}
                    >
                      (transaction date)
                    </span>
                  </label>
                  <DateInput
                    value={form.cashedOn}
                    onChange={(e) => set("cashedOn", e.target.value)}
                    maxYear={new Date().getFullYear()}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">
                    Maker / Payer Name
                    <span style={{ color: "var(--danger)", marginLeft: 3 }}>
                      *
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "var(--primary)",
                        marginLeft: 6,
                      }}
                    >
                      (from Company Master)
                    </span>
                  </label>
                  <MasterDropdown
                    value={form.maker}
                    onChange={(val) => set("maker", val)}
                    items={companyList}
                    placeholder="Select or type company name…"
                    icon="🏢"
                    onAddNew={handleAddToCompanyMaster}
                    renderBadge={(item) =>
                      item.isBlocked ? (
                        <span
                          style={{
                            fontSize: 10,
                            background: "#fef2f2",
                            color: "#b91c1c",
                            padding: "2px 7px",
                            borderRadius: 10,
                            fontWeight: 700,
                          }}
                        >
                          Blocked
                        </span>
                      ) : null
                    }
                  />

                  {/* ── Blocked maker warning ── */}
                  {blockedMaker ? (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "10px 14px",
                        background: "#fef2f2",
                        border: "1.5px solid #fca5a5",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 18, flexShrink: 0 }}>⛔</span>
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "#b91c1c",
                          }}
                        >
                          Blocked Company — Transaction Not Allowed
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#7f1d1d",
                            marginTop: 2,
                          }}
                        >
                          <strong>"{blockedMaker.name}"</strong> is on the
                          blocked companies list.
                          {blockedMaker.blockedReason && (
                            <span> Reason: {blockedMaker.blockedReason}.</span>
                          )}{" "}
                          This check cannot be cashed.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--gray-1)",
                        marginTop: 3,
                      }}
                    >
                      The business/person who wrote the check
                    </div>
                  )}
                </div>
              </div>

              {/* Amount + Fee + Payout */}
              <div
                style={{
                  background: "var(--gray-4)",
                  borderRadius: 10,
                  padding: "16px",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 12,
                  }}
                >
                  💰 Amount Calculation
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div>
                    <label className="form-label">
                      Check Amount ($){" "}
                      <span style={{ color: "var(--danger)" }}>*</span>
                    </label>
                    <input
                      className="form-control"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={(e) => set("amount", e.target.value)}
                      placeholder="130.00"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 800,
                        fontSize: 18,
                        color: "var(--dark)",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      className="form-label"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      Fee ($) <span style={{ color: "var(--danger)" }}>*</span>
                      <button
                        type="button"
                        style={{
                          fontSize: 10,
                          color: "var(--primary)",
                          fontWeight: 600,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        onClick={autoCalcFee}
                      >
                        Auto (2%)
                      </button>
                    </label>
                    <input
                      className="form-control"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.fee}
                      onChange={(e) => set("fee", e.target.value)}
                      placeholder="8.00"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: 18,
                        color: "var(--danger)",
                      }}
                    />
                  </div>
                  <div>
                    <label className="form-label">Customer Pay Out</label>
                    <div
                      style={{
                        padding: "10px 14px",
                        background: payOut
                          ? "var(--success-light)"
                          : "var(--gray-3)",
                        borderRadius: "var(--radius-sm)",
                        fontFamily: "var(--font-display)",
                        fontWeight: 900,
                        fontSize: 22,
                        color: payOut ? "var(--success)" : "var(--gray-2)",
                        textAlign: "center",
                      }}
                    >
                      {payOut ? fmt(payOut) : "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Maker Address — hidden */}
              {/* License / ID # — hidden */}

              {/* Status — hidden */}
            </div>
          </div>

          {/* STEP 3 — BANK INFO */}
          <div className="card">
            <div className="card-header">
              <h3>🏦 Bank Information</h3>
              <span style={{ fontSize: 12, color: "var(--gray-1)" }}>
                From MICR line at bottom of check
              </span>
            </div>
            <div className="card-body">
              {/* Bank display row — dropdowns hidden, values saved as-is */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                {/* Bank Name display */}
                <div style={{ flex: "1 1 180px" }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--gray-1)",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 3,
                    }}
                  >
                    Bank Name{" "}
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--primary)",
                        fontWeight: 500,
                        textTransform: "none",
                      }}
                    >
                      (from Bank Master)
                    </span>
                  </div>
                  {form.bank.name ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 12px",
                        background: "var(--gray-4)",
                        borderRadius: 8,
                        border: "1px solid var(--gray-3)",
                      }}
                    >
                      <span style={{ fontSize: 16 }}>🏦</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: "var(--dark)",
                          flex: 1,
                        }}
                      >
                        {form.bank.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setBank("name", "")}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 11,
                          color: "var(--gray-1)",
                          padding: "1px 4px",
                        }}
                        title="Clear bank"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "7px 12px",
                        background: "var(--gray-4)",
                        borderRadius: 8,
                        border: "1px dashed var(--gray-3)",
                        fontSize: 12,
                        color: "var(--gray-2)",
                      }}
                    >
                      Auto-filled from scan
                    </div>
                  )}
                </div>

                {/* Account Number display */}
                <div style={{ flex: "0 1 160px" }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--gray-1)",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 3,
                    }}
                  >
                    Account Number
                  </div>
                  {form.bank.accountNumber ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 12px",
                        background: "var(--gray-4)",
                        borderRadius: 8,
                        border: "1px solid var(--gray-3)",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "var(--dark)",
                          letterSpacing: 1,
                        }}
                      >
                        {form.bank.accountNumber}
                      </span>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "7px 12px",
                        background: "var(--gray-4)",
                        borderRadius: 8,
                        border: "1px dashed var(--gray-3)",
                        fontSize: 12,
                        color: "var(--gray-2)",
                      }}
                    >
                      Auto-filled from scan
                    </div>
                  )}
                </div>

                {/* Routing Number — stored in state but not shown */}
                {/* Hidden inputs keep values in form state for saving */}
              </div>

              {/* Show MasterDropdown only when bank name is cleared */}
              {!form.bank.name && (
                <div style={{ marginTop: 10 }}>
                  <MasterDropdown
                    value={form.bank.name}
                    onChange={(val, item) => {
                      setBank("name", val);
                      if (item?.routingNumber)
                        setBank("routingNumber", item.routingNumber);
                    }}
                    items={bankList}
                    placeholder="Select or type bank name…"
                    icon="🏦"
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 5,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "var(--gray-1)" }}>
                      Bank not found?
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        color: "var(--primary)",
                        fontWeight: 700,
                      }}
                      onClick={() => setShowAddBank(true)}
                    >
                      ➕ Add New Bank
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Verification & Notes — hidden */}
        </div>

        {/* ── RIGHT PANEL — STICKY SUMMARY ── */}
        <div
          className="cashcheck-right-panel"
          style={{
            position: "sticky",
            top: 80,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* Transaction receipt preview */}
          <div
            className="card"
            style={{
              background: "var(--dark)",
              border: "none",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div
                style={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Transaction Preview
              </div>
            </div>
            <div style={{ padding: "18px 20px" }}>
              {/* Customer */}
              <div
                style={{
                  marginBottom: 16,
                  paddingBottom: 14,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: "rgba(255,255,255,0.4)",
                    marginBottom: 4,
                  }}
                >
                  PAY TO
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    fontSize: 16,
                    color: "white",
                  }}
                >
                  {selectedCustomer
                    ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                    : "— Select Customer —"}
                </div>
              </div>

              {/* Check # */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Check #</span>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    color: "var(--accent)",
                    fontSize: 15,
                  }}
                >
                  {form.checkNumber || "—"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Date</span>
                <span style={{ color: "white" }}>
                  {form.cashedOn || form.checkDate || "—"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Maker</span>
                <span
                  style={{
                    color: "white",
                    textAlign: "right",
                    maxWidth: 160,
                    fontSize: 12,
                  }}
                >
                  {form.maker || "—"}
                </span>
              </div>

              <div
                style={{
                  height: 1,
                  background: "rgba(255,255,255,0.08)",
                  margin: "14px 0",
                }}
              />

              {/* Amounts */}
              {[
                {
                  label: "Check Amount",
                  val: form.amount ? fmt(form.amount) : "—",
                  color: "white",
                  size: 16,
                },
                {
                  label: "Fee",
                  val: form.fee !== "" ? `- ${fmt(form.fee)}` : "—",
                  color: "var(--danger)",
                  size: 14,
                },
              ].map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}
                  >
                    {r.label}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      color: r.color,
                      fontSize: r.size,
                    }}
                  >
                    {r.val}
                  </span>
                </div>
              ))}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 14px",
                  background: payOut
                    ? "rgba(16,185,129,0.15)"
                    : "rgba(255,255,255,0.05)",
                  borderRadius: 10,
                  border: `1.5px solid ${payOut ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 700,
                  }}
                >
                  CUSTOMER PAYOUT
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 900,
                    fontSize: 26,
                    color: payOut ? "#10b981" : "rgba(255,255,255,0.2)",
                  }}
                >
                  {payOut ? fmt(payOut) : "—"}
                </span>
              </div>

              {form.isLoan && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    background: "rgba(224,60,60,0.15)",
                    border: "1px solid rgba(224,60,60,0.3)",
                    borderRadius: 8,
                    fontSize: 12.5,
                    color: "#f87171",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  🔴 LOAN / PAYDAY ADVANCE
                </div>
              )}

              {/* Check images thumbnails */}
              {(checkFrontPreview || checkBackPreview) && (
                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  {checkFrontPreview && (
                    <img
                      src={checkFrontPreview}
                      alt="Front"
                      style={{
                        flex: 1,
                        height: 50,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    />
                  )}
                  {checkBackPreview && (
                    <img
                      src={checkBackPreview}
                      alt="Back"
                      style={{
                        flex: 1,
                        height: 50,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            className="btn btn-lg w-full"
            onClick={handleSubmit}
            disabled={loading || !!blockedMaker}
            style={{
              fontSize: 16,
              background: blockedMaker ? "#b91c1c" : "var(--primary)",
              color: "white",
              border: "none",
              opacity: blockedMaker ? 0.85 : 1,
              cursor: blockedMaker ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
            title={
              blockedMaker
                ? `Blocked: "${blockedMaker.name}" is on the blocked companies list`
                : undefined
            }
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="spinner" /> Processing...
              </span>
            ) : blockedMaker ? (
              <>⛔ Transaction Blocked — Company Blacklisted</>
            ) : (
              "💵 Complete Transaction"
            )}
          </button>
          <button
            className="btn btn-outline w-full"
            onClick={() => navigate("/cheques")}
          >
            Cancel
          </button>

          {/* Quick tips */}
          <div
            style={{
              padding: "14px",
              background: "var(--accent-light)",
              borderRadius: 10,
              border: "1px solid rgba(240,165,0,0.2)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#92400e",
                marginBottom: 8,
              }}
            >
              📋 CASHIER CHECKLIST
            </div>
            {[
              "Verify customer ID matches check name",
              "Check ID is not expired",
              "Verify check signature on back",
              "Confirm MICR routing/account numbers",
              "Photograph front and back of check",
            ].map((tip, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 8,
                  fontSize: 11.5,
                  color: "#78350f",
                  marginBottom: 4,
                }}
              >
                <span>□</span>
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
